/**
 * Section type registry.
 *
 * Each entry decides what the Page Editor renders for a section:
 *   fields   – which section-level fields are shown up front
 *   items    – whether the section has repeatable children, and their fields
 *   settings – the typed keys stored in the section's `settings` JSON column
 *
 * Adding a new section type is a matter of adding one entry here — no editor
 * changes required. Keep `value` in sync with the backend's allowed type list.
 */

export const SECTION_FIELDS = {
  title: { label: 'Title', type: 'text', placeholder: 'Section heading' },
  subtitle: { label: 'Subtitle', type: 'text', placeholder: 'Supporting line under the heading' },
  body: { label: 'Body', type: 'html', placeholder: 'Write the section content…' },
  image: { label: 'Image', type: 'image' },
  backgroundImage: { label: 'Background Image', type: 'image' },
  ctaLabel: { label: 'Button Label', type: 'text', placeholder: 'Get a Quote' },
  ctaUrl: { label: 'Button Link', type: 'text', placeholder: '/contact-us' },
};

export const ITEM_FIELDS = {
  title: { label: 'Title', type: 'text', placeholder: 'Item title' },
  subtitle: { label: 'Subtitle', type: 'text', placeholder: 'Item subtitle' },
  description: { label: 'Description', type: 'textarea', placeholder: 'Short description' },
  icon: { label: 'Icon', type: 'icon' },
  image: { label: 'Image', type: 'image' },
  value: { label: 'Value', type: 'text', placeholder: 'e.g. 250+' },
  linkLabel: { label: 'Link Label', type: 'text', placeholder: 'Learn More' },
  linkUrl: { label: 'Link URL', type: 'text', placeholder: '/about-us' },
};

const ALIGN_SETTING = {
  key: 'align',
  label: 'Alignment',
  type: 'select',
  options: ['left', 'center', 'right'],
  default: 'center',
};

const COLUMNS_SETTING = {
  key: 'columns',
  label: 'Columns',
  type: 'select',
  options: ['2', '3', '4'],
  default: '3',
};

const THEME_SETTING = {
  key: 'theme',
  label: 'Theme',
  type: 'select',
  options: ['light', 'dark', 'brand'],
  default: 'light',
};

export const SECTION_TYPES = [
  {
    value: 'hero',
    label: 'Hero Banner',
    hint: 'Full-width banner with headline, button and optional slides.',
    fields: ['title', 'subtitle', 'body', 'image', 'backgroundImage', 'ctaLabel', 'ctaUrl'],
    items: {
      enabled: true,
      label: 'Slide',
      plural: 'Slides',
      fields: ['title', 'subtitle', 'description', 'image', 'linkLabel', 'linkUrl'],
      primary: 'title',
      media: 'image',
    },
    settings: [
      ALIGN_SETTING,
      { key: 'overlay', label: 'Dark overlay', type: 'boolean', default: true },
      { key: 'height', label: 'Height', type: 'select', options: ['auto', 'medium', 'large', 'full'], default: 'large' },
      { key: 'autoplay', label: 'Autoplay slides', type: 'boolean', default: true },
    ],
  },
  {
    value: 'rich_text',
    label: 'Rich Text',
    hint: 'A formatted block of copy — ideal for policy and long-form pages.',
    fields: ['title', 'subtitle', 'body'],
    items: { enabled: false },
    settings: [ALIGN_SETTING, { key: 'width', label: 'Content width', type: 'select', options: ['narrow', 'normal', 'wide'], default: 'normal' }],
  },
  {
    value: 'image_text',
    label: 'Image + Text',
    hint: 'Two-column block pairing an image with copy and a button.',
    fields: ['title', 'subtitle', 'body', 'image', 'ctaLabel', 'ctaUrl'],
    items: { enabled: false },
    settings: [
      { key: 'image_position', label: 'Image position', type: 'select', options: ['left', 'right'], default: 'left' },
      THEME_SETTING,
    ],
  },
  {
    value: 'stats',
    label: 'Stats / Counters',
    hint: 'Animated number counters such as “250+ Projects”.',
    fields: ['title', 'subtitle', 'backgroundImage'],
    items: {
      enabled: true,
      label: 'Counter',
      plural: 'Counters',
      fields: ['value', 'title', 'subtitle', 'icon'],
      primary: 'title',
      badge: 'value',
    },
    settings: [COLUMNS_SETTING, THEME_SETTING],
  },
  {
    value: 'cards',
    label: 'Cards Grid',
    hint: 'Grid of icon or image cards — services, features, “why choose us”.',
    fields: ['title', 'subtitle', 'body'],
    items: {
      enabled: true,
      label: 'Card',
      plural: 'Cards',
      fields: ['title', 'description', 'icon', 'image', 'linkLabel', 'linkUrl'],
      primary: 'title',
      media: 'image',
    },
    settings: [COLUMNS_SETTING, ALIGN_SETTING, { key: 'style', label: 'Card style', type: 'select', options: ['flat', 'bordered', 'elevated'], default: 'elevated' }],
  },
  {
    value: 'gallery',
    label: 'Gallery',
    hint: 'Image grid with optional captions and lightbox.',
    fields: ['title', 'subtitle'],
    items: {
      enabled: true,
      label: 'Image',
      plural: 'Images',
      fields: ['image', 'title', 'subtitle', 'linkUrl'],
      primary: 'title',
      media: 'image',
    },
    settings: [COLUMNS_SETTING, { key: 'lightbox', label: 'Enable lightbox', type: 'boolean', default: true }],
  },
  {
    value: 'cta',
    label: 'Call to Action',
    hint: 'A focused strip with a headline and a single button.',
    fields: ['title', 'subtitle', 'body', 'backgroundImage', 'ctaLabel', 'ctaUrl'],
    items: { enabled: false },
    settings: [ALIGN_SETTING, THEME_SETTING, { key: 'overlay', label: 'Dark overlay', type: 'boolean', default: false }],
  },
  {
    value: 'faq',
    label: 'FAQ',
    hint: 'Accordion of questions and answers.',
    fields: ['title', 'subtitle'],
    items: {
      enabled: true,
      label: 'Question',
      plural: 'Questions',
      fields: ['title', 'description'],
      primary: 'title',
    },
    settings: [{ key: 'open_first', label: 'Open first answer', type: 'boolean', default: true }],
  },
  {
    value: 'testimonials',
    label: 'Testimonials',
    hint: 'Client quotes with name, role and photo.',
    fields: ['title', 'subtitle', 'backgroundImage'],
    items: {
      enabled: true,
      label: 'Testimonial',
      plural: 'Testimonials',
      fields: ['title', 'subtitle', 'description', 'image', 'value'],
      primary: 'title',
      media: 'image',
    },
    settings: [
      { key: 'layout', label: 'Layout', type: 'select', options: ['slider', 'grid'], default: 'slider' },
      COLUMNS_SETTING,
    ],
  },
  {
    value: 'contact_info',
    label: 'Contact Info',
    hint: 'Address, phone and email tiles, plus an optional map embed.',
    fields: ['title', 'subtitle', 'body'],
    items: {
      enabled: true,
      label: 'Detail',
      plural: 'Details',
      fields: ['title', 'description', 'icon', 'linkUrl'],
      primary: 'title',
    },
    settings: [
      COLUMNS_SETTING,
      { key: 'show_map', label: 'Show map embed', type: 'boolean', default: true },
    ],
  },
  {
    value: 'raw_html',
    label: 'Raw HTML',
    hint: 'Custom markup or an embed. Use only when the other types will not do.',
    fields: ['title', 'body'],
    items: { enabled: false },
    settings: [{ key: 'full_width', label: 'Full width', type: 'boolean', default: false }],
  },
];

export const SECTION_TYPE_OPTIONS = SECTION_TYPES.map((type) => ({
  value: type.value,
  label: type.label,
}));

const FALLBACK_TYPE = {
  value: 'rich_text',
  label: 'Rich Text',
  hint: '',
  fields: ['title', 'subtitle', 'body', 'image'],
  items: { enabled: false },
  settings: [],
};

export const getSectionType = (value) =>
  SECTION_TYPES.find((type) => type.value === value) || { ...FALLBACK_TYPE, value: value || 'rich_text', label: value || 'Section' };

export const PAGE_TEMPLATES = [
  { value: 'default', label: 'Default' },
  { value: 'home', label: 'Home' },
  { value: 'contact', label: 'Contact' },
  { value: 'listing', label: 'Listing' },
  { value: 'legal', label: 'Legal' },
];

/** react-icons/fa names, matching the convention already used by Services. */
export const ICON_OPTIONS = [
  'FaBuilding', 'FaCity', 'FaHome', 'FaWarehouse', 'FaIndustry', 'FaLandmark',
  'FaHardHat', 'FaDraftingCompass', 'FaRulerCombined', 'FaCog', 'FaCogs', 'FaTools',
  'FaRoad', 'FaMapMarkedAlt', 'FaMapMarkerAlt', 'FaTasks', 'FaClipboardList', 'FaProjectDiagram',
  'FaCouch', 'FaPaintRoller', 'FaLightbulb', 'FaLeaf', 'FaShieldAlt', 'FaAward',
  'FaCheckCircle', 'FaStar', 'FaUsers', 'FaUserTie', 'FaHandshake', 'FaChartLine',
  'FaClock', 'FaPhoneAlt', 'FaEnvelope', 'FaWhatsapp', 'FaGlobe', 'FaComments',
];

export const ITEM_FIELD_LABEL_OVERRIDES = {
  faq: { title: 'Question', description: 'Answer' },
  testimonials: { title: 'Client Name', subtitle: 'Role / Company', description: 'Quote', value: 'Rating (1-5)', image: 'Photo' },
  stats: { value: 'Number', title: 'Label', subtitle: 'Caption' },
  contact_info: { title: 'Label', description: 'Value', linkUrl: 'Link' },
  gallery: { title: 'Caption', subtitle: 'Category', linkUrl: 'Link' },
};

/** Field label for an item field, respecting per-type wording. */
export const getItemFieldLabel = (sectionType, field) =>
  ITEM_FIELD_LABEL_OVERRIDES[sectionType]?.[field] || ITEM_FIELDS[field]?.label || field;
